import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, IconButton, Slider } from "rsuite";
import {
  PlayOutline,
  PauseOutline,
  ArowBack,
  ArrowRight,
  Close,
  Speaker,
} from "@rsuite/icons";
import { useMediaContext } from "../../../context/MediaContext";
import "./AudioPlayer.css";

const AudioPlayer: React.FC = () => {
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const playingMediaRef = useRef<HTMLMediaElement | null>(null);
  const previousMediaUrlRef = useRef<string>("");
  const volumeRef = useRef<HTMLDivElement | null>(null);

  const [volume, setVolume] = useState(70);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [title, setTitle] = useState<string>("");
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [showVolume, setShowVolume] = useState(false);
  const [playError, setPlayError] = useState<boolean>(false);
  const isVideo = playingMediaInfo?.media_type === "video";

  const setPlayingMediaElement = useCallback((element: HTMLMediaElement | null) => {
    playingMediaRef.current = element;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (volumeRef.current && !volumeRef.current.contains(event.target as Node)) {
        setShowVolume(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const media = playingMediaRef.current;
    if (!isVisible || !media) return;

    const handleTimeUpdate = () => {
      setCurrentTime(media.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(Number.isFinite(media.duration) ? media.duration : 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    media.addEventListener("timeupdate", handleTimeUpdate);
    media.addEventListener("loadedmetadata", handleLoadedMetadata);
    media.addEventListener("durationchange", handleLoadedMetadata);
    media.addEventListener("ended", handleEnded);

    if (media.readyState >= 1) {
      handleLoadedMetadata();
    }

    return () => {
      media.removeEventListener("timeupdate", handleTimeUpdate);
      media.removeEventListener("loadedmetadata", handleLoadedMetadata);
      media.removeEventListener("durationchange", handleLoadedMetadata);
      media.removeEventListener("ended", handleEnded);
    };
  }, [isVisible, isVideo, playingMediaInfo?.media_url, setIsPlaying]);

  useEffect(() => {
    if (!playingMediaInfo?.media_url) {
      playingMediaRef.current?.pause();
      previousMediaUrlRef.current = "";
      setIsVisible(false);
      setCurrentTime(0);
      setDuration(0);
      return;
    }

    setTitle(playingMediaInfo?.title || "Now Playing");
    setIsVisible(true);
    setPlayError(false);
  }, [playingMediaInfo]);

  useEffect(() => {
    if (!isVisible || !playingMediaInfo?.media_url) return;

    if (isPlaying) {
      void forcePlay();
    } else {
      playingMediaRef.current?.pause();
    }
  }, [isVisible, isVideo, playingMediaInfo?.media_url, isPlaying]);

  const forcePlay = async () => {
    const media = playingMediaRef.current;
    if (!media || !playingMediaInfo?.media_url) return;

    try {
      const sourceChanged = previousMediaUrlRef.current !== playingMediaInfo.media_url;
      if (sourceChanged) {
        media.pause();
        media.src = playingMediaInfo.media_url;
        media.currentTime = 0;
        previousMediaUrlRef.current = playingMediaInfo.media_url;
        setCurrentTime(0);
        setDuration(0);
        media.load();
      }
      media.volume = volume / 100;
      await media.play();
      setIsPlaying(true);
    } catch (error) {
      setPlayError(true);
      setIsPlaying(false);
    }
  };

  const togglePlayPause = async () => {
    const media = playingMediaRef.current;
    if (!media) return;

    try {
      if (isPlaying) {
        media.pause();
        setIsPlaying(false);
      } else {
        await media.play();
        setIsPlaying(true);
      }
    } catch (error) {
      setPlayError(true);
      setIsPlaying(false);
    }
  };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const media = playingMediaRef.current;
    const nextTime = Number(event.target.value);
    if (!media) return;
    media.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const jumpTime = (seconds: number) => {
    const media = playingMediaRef.current;
    if (!media) return;
    const nextTime = Math.max(0, Math.min(media.currentTime + seconds, duration || 0));
    media.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const handleVolume = (value: number) => {
    const media = playingMediaRef.current;
    if (media) {
      media.volume = value / 100;
    }
    setVolume(value);
  };

  const handleHide = () => {
    playingMediaRef.current?.pause();
    previousMediaUrlRef.current = "";
    setIsPlaying(false);
    setPlayingMediaInfo({});
    setIsVisible(false);
    setShowVolume(false);
  };

  const handlePlayError = () => {
    setPlayError(true);
    setIsPlaying(false);
  };

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds)) return "00:00";

    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}:${String(remainingSeconds).padStart(2, "0")}`;
    }

    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  if (!isVisible) {
    return (
      <audio
        ref={setPlayingMediaElement}
        src=""
        preload="metadata"
        onError={handlePlayError}
      />
    );
  }

  return (
    <>
      {!isVideo && (
        <audio
          ref={setPlayingMediaElement}
          src=""
          preload="metadata"
          onError={handlePlayError}
        />
      )}
      <div className="audio-player-overlay" onClick={handleHide}>
        <div
          className={`audio-player-modal ${isVideo ? "audio-player-modal-video" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="audio-player-close" onClick={handleHide}>
            <Close />
          </button>

          <div className="audio-player-artwork">
            {isVideo ? (
              <video
                ref={setPlayingMediaElement}
                src={playingMediaInfo?.media_url || ""}
                poster={playingMediaInfo?.media_image_url || ""}
                preload="metadata"
                autoPlay={isPlaying}
                playsInline
                className="audio-player-video"
                onError={handlePlayError}
              />
            ) : (
              <>
                <div className="audio-player-disc">
                  <div className="audio-player-disc-core" />
                </div>
                <div className="audio-player-wave">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </>
            )}
          </div>

          <div className="audio-player-content">
            <div className="audio-player-kicker">Now Playing</div>
            <div className="audio-player-title" title={title}>
              {title || "Audio Preview"}
            </div>
            <div className="audio-player-status">
              {playError
                ? `This ${isVideo ? "video" : "audio"} could not be played.`
                : isPlaying
                ? "Playback in progress"
                : "Playback paused"}
            </div>

            <div className="audio-player-progress-wrap">
              <input
                className="audio-player-progress"
                type="range"
                min={0}
                max={duration || 0}
                step={1}
                value={Math.min(currentTime, duration || 0)}
                onChange={handleSeek}
              />
              <div className="audio-player-times">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div className="audio-player-controls">
              <IconButton
                circle
                appearance="subtle"
                icon={<ArowBack />}
                onClick={() => jumpTime(-10)}
              />
              <Button
                appearance="primary"
                className="audio-player-main-btn"
                onClick={togglePlayPause}
              >
                {isPlaying && !playError ? <PauseOutline /> : <PlayOutline />}
              </Button>
              <IconButton
                circle
                appearance="subtle"
                icon={<ArrowRight />}
                onClick={() => jumpTime(10)}
              />
              <div className="audio-player-volume" ref={volumeRef}>
                <IconButton
                  circle
                  appearance="subtle"
                  icon={<Speaker />}
                  onClick={() => setShowVolume(!showVolume)}
                />
                {showVolume && (
                  <div className="audio-player-volume-pop">
                    <div className="audio-player-volume-label">Volume</div>
                    <Slider
                      progress
                      min={0}
                      max={100}
                      value={volume}
                      onChange={handleVolume}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AudioPlayer;
